import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Button, Collapse, Grid, InputAdornment, MenuItem, Paper, Stack,
  TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { ArrowBack, FilterList, ClearAll, PlayArrow, Search, Visibility } from '@mui/icons-material';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import PartnerMultiSelect from '../../components/partners/PartnerMultiSelect';
import {
  getReportDefinitions, runReport, getTripRequestReport, getPartnerWiseReport,
  type ReportDefinition,
} from '../../services/reportService';
import { listZones } from '../../services/zoneService';
import type { PartnerRow } from '../../services/partnerUserService';
import { opsEntityPath } from '../../utils/opsEntityNav';
import { formatMoney } from '../../utils/currency';

const TRIP_STATUSES = ['searching', 'accepted', 'started', 'completed', 'cancelled'];
const PAYMENT_OPTIONS = ['cash', 'card', 'upi', 'wallet', 'corporate'];
const TRIP_TYPES = ['LOCAL', 'RENTAL', 'OUTSTATION'];

export default function ReportRunPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { slug = '' } = useParams<{ slug: string }>();
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [partners, setPartners] = useState<PartnerRow[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [applied, setApplied] = useState(0);

  const { data: definitions } = useQuery({ queryKey: ['report-definitions'], queryFn: getReportDefinitions });
  const selected = (definitions || []).find((r) => r.slug === slug) as ReportDefinition | undefined;

  const isTripRequest = slug === 'trip-request-report';
  const isPartnerWise = slug === 'partner-wise-report';
  const isOperational = isTripRequest || isPartnerWise;

  const { data: zonesData } = useQuery({
    queryKey: ['zones-list-mini'],
    queryFn: () => listZones({ limit: 100 }),
  });
  const zones = zonesData?.rows || [];

  const queryParams = useMemo(() => {
    const driverIds = partners.map((p) => p._id).join(',');
    return {
      ...filters,
      ...(driverIds ? { driverIds } : {}),
    };
  }, [filters, partners]);

  const extraFilterCount = [
    filters.dateFrom, filters.dateTo, filters.status, filters.paymentMethod,
    filters.tripType, filters.zoneId, filters.search, filters.customerUsername,
    filters.driverUsername, filters.tripId, filters.orderId,
    partners.length ? 'p' : '',
  ].filter(Boolean).length;

  const { data: result, isFetching, refetch } = useQuery({
    queryKey: ['report-run', slug, queryParams, applied],
    queryFn: () => {
      if (isTripRequest) return getTripRequestReport(queryParams);
      if (isPartnerWise) return getPartnerWiseReport(queryParams);
      return runReport(slug, queryParams);
    },
    enabled: !!slug && (isOperational || !!selected),
  });

  const openRow = (row: Record<string, unknown>) => {
    const path = opsEntityPath(row, { slug, dataSource: result?.meta?.dataSource });
    if (path) navigate(path, { state: { from: `/reports/${slug}` } });
  };

  const columns: GridColDef[] = useMemo(() => {
    const cols = result?.columns || selected?.columns || [];
    const mapped: GridColDef[] = cols.map((c) => {
      const col: GridColDef = {
        field: c.key,
        headerName: c.label,
        flex: c.type === 'string' ? 1 : undefined,
        minWidth: c.type === 'string' ? 140 : 110,
        width: c.width || (c.type === 'currency' || c.type === 'number' ? 120 : c.type === 'date' ? 170 : undefined),
        valueFormatter: c.type === 'currency'
          ? (v, row) => (v != null ? `${formatMoney(v, row)}` : '—')
          : c.type === 'date'
            ? (v) => (v ? new Date(v as string).toLocaleString() : '—')
            : undefined,
      };
      if (c.key === 'customer') {
        col.renderCell = (p) => {
          const id = String((p.row as Record<string, unknown>).customerId || '');
          if (!id) return String(p.value ?? '—');
          return (
            <Button size="small" sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0, px: 0 }}
              onClick={(e) => { e.stopPropagation(); navigate(`/customers/${id}`, { state: { from: `/reports/${slug}` } }); }}>
              {String(p.value ?? '—')}
            </Button>
          );
        };
      }
      if (c.key === 'driver' || c.key === 'partner') {
        col.renderCell = (p) => {
          const id = String((p.row as Record<string, unknown>).driverId || (isPartnerWise ? p.row._id : '') || '');
          if (!id || String(p.value) === 'Unassigned' || String(p.value) === '-') return String(p.value ?? '—');
          return (
            <Button size="small" sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0, px: 0 }}
              onClick={(e) => { e.stopPropagation(); navigate(`/partners/${id}`, { state: { from: `/reports/${slug}` } }); }}>
              {String(p.value ?? '—')}
            </Button>
          );
        };
      }
      return col;
    });
    mapped.push({
      field: 'actions',
      type: 'actions',
      headerName: 'View',
      width: 80,
      getActions: (p) => {
        const path = opsEntityPath(p.row as Record<string, unknown>, { slug, dataSource: result?.meta?.dataSource });
        if (!path) return [];
        return [
          <GridActionsCellItem
            key="view"
            icon={<Visibility fontSize="small" />}
            label="View"
            onClick={() => openRow(p.row as Record<string, unknown>)}
          />,
        ];
      },
    });
    return mapped;
  }, [result, selected, slug, isPartnerWise, navigate]);

  const patch = (key: string, value: string) => setFilters((prev) => ({ ...prev, [key]: value }));
  const clearFilters = () => {
    setFilters({});
    setPartners([]);
    setApplied((n) => n + 1);
  };

  const title = selected?.name || (isTripRequest ? 'Trip Request Report' : isPartnerWise ? 'Partner Wise Report' : slug);
  const statusOptions = isOperational ? TRIP_STATUSES : [];

  return (
    <PageShell>
      <PageHeader
        eyebrow="Reports"
        title={title}
        subtitle={selected ? `${selected.slug} · ${selected.module}` : 'Filtered report with search and export'}
        breadcrumbs={[
          { label: 'Reports', href: '/reports' },
          { label: title },
        ]}
        actions={
          <Button startIcon={<ArrowBack />} variant="outlined" onClick={() => navigate('/reports')}>
            All reports
          </Button>
        }
      />

      <Paper elevation={0} sx={{ mb: 2, borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'center' }}
          sx={{ px: 2, py: 1.5 }}
        >
          <TextField
            size="small"
            placeholder={isTripRequest ? 'Search request #, pickup, drop…' : isPartnerWise ? 'Search partner, email, phone…' : 'Search…'}
            value={filters.search || ''}
            onChange={(e) => patch('search', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 220, maxWidth: 480 }}
          />
          <Button
            size="small"
            variant={filtersOpen || extraFilterCount > 0 ? 'contained' : 'outlined'}
            startIcon={<FilterList />}
            onClick={() => setFiltersOpen((v) => !v)}
          >
            Filters{extraFilterCount > 0 ? ` (${extraFilterCount})` : ''}
          </Button>
          <Button size="small" variant="contained" startIcon={<PlayArrow />} onClick={() => { setApplied((n) => n + 1); refetch(); }} disabled={isFetching}>
            Run
          </Button>
        </Stack>

        <Collapse in={filtersOpen} unmountOnExit>
          <Box sx={{ px: 2, pb: 2, borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={800}>Advanced filters</Typography>
              {extraFilterCount > 0 && (
                <Button size="small" startIcon={<ClearAll />} onClick={clearFilters}>Clear</Button>
              )}
            </Stack>
            <Grid container spacing={1.5}>
              {isOperational ? (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth size="small" type="date" label="From date" InputLabelProps={{ shrink: true }}
                      value={filters.dateFrom || ''} onChange={(e) => patch('dateFrom', e.target.value)}
                      sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth size="small" type="date" label="To date" InputLabelProps={{ shrink: true }}
                      value={filters.dateTo || ''} onChange={(e) => patch('dateTo', e.target.value)}
                      sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField select fullWidth size="small" label={isPartnerWise ? 'Trip status' : 'Status'}
                      value={filters.status || ''} onChange={(e) => patch('status', e.target.value)}
                      sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}>
                      <MenuItem value="">All statuses</MenuItem>
                      {TRIP_STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField select fullWidth size="small" label="Zone"
                      value={filters.zoneId || ''} onChange={(e) => patch('zoneId', e.target.value)}
                      sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}>
                      <MenuItem value="">All zones</MenuItem>
                      {zones.map((z) => <MenuItem key={z._id} value={z._id}>{z.name}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <PartnerMultiSelect
                      value={partners}
                      onChange={setPartners}
                      entityType="driver"
                      label={isTripRequest ? 'Partner / Driver' : 'Partners'}
                      placeholder="Select one or more"
                    />
                  </Grid>
                  {isTripRequest && (
                    <>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField select fullWidth size="small" label="Payment"
                          value={filters.paymentMethod || ''} onChange={(e) => patch('paymentMethod', e.target.value)}
                          sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}>
                          <MenuItem value="">All payments</MenuItem>
                          {PAYMENT_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField select fullWidth size="small" label="Trip type"
                          value={filters.tripType || ''} onChange={(e) => patch('tripType', e.target.value)}
                          sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}>
                          <MenuItem value="">All trip types</MenuItem>
                          {TRIP_TYPES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                      </Grid>
                    </>
                  )}
                </>
              ) : (
                (selected?.filters || []).map((f) => (
                  <Grid item xs={12} sm={6} md={3} key={String(f.key)}>
                    <TextField
                      fullWidth size="small"
                      label={String(f.label || f.key)}
                      type={f.type === 'date' ? 'date' : 'text'}
                      InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
                      value={filters[String(f.key)] || ''}
                      onChange={(e) => patch(String(f.key), e.target.value)}
                      sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
                    />
                  </Grid>
                ))
              )}

              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Customer username"
                  placeholder="Name, email, or phone"
                  value={filters.customerUsername || ''} onChange={(e) => patch('customerUsername', e.target.value)}
                  sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Driver username"
                  placeholder="Name, email, or phone"
                  value={filters.driverUsername || ''} onChange={(e) => patch('driverUsername', e.target.value)}
                  sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Trip ID"
                  placeholder="Request # or trip id"
                  value={filters.tripId || ''} onChange={(e) => patch('tripId', e.target.value)}
                  sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Order ID"
                  placeholder="Order # or booking #"
                  value={filters.orderId || ''} onChange={(e) => patch('orderId', e.target.value)}
                  sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }} />
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden', p: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ px: 1, pb: 1 }}>
          {result?.meta?.total ?? 0} rows
          {result?.meta?.dataSource ? ` · ${result.meta.dataSource}` : ''}
          {' · Click a row or View to open customer, trip, order, or partner details'}
        </Typography>
        <EnhancedDataGrid
          rows={result?.rows || []}
          columns={columns}
          loading={isFetching}
          getRowId={(r) => String((r as Record<string, unknown>)._id ?? (r as Record<string, unknown>).id ?? JSON.stringify(r))}
          autoHeight
          exportFileName={slug || 'report'}
          statusField={isOperational ? 'status' : undefined}
          statusOptions={statusOptions.map((s) => ({ value: s, label: s.replace(/_/g, ' ') }))}
          searchPlaceholder="Search results…"
          onRowClick={(p) => openRow(p.row as Record<string, unknown>)}
        />
      </Paper>
    </PageShell>
  );
}
